import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'

import EditableItemFooter from './EditableItemFooter'
import OwnBookingFooter from './OwnBookingFooter'
import ReadOnlyItemFooter from './ReadOnlyItemFooter'

import TextButton from 'LuxKit/Button/TextButton'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { getCurrentUserId } from 'selectors/accountSelectors'
import FooterWithCreateUpdateInfo from 'tripPlanner/components/TripModal/Common/FooterWithCreateUpdateInfo'
import { useExperience } from 'tripPlanner/hooks/api/offer'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import type { ExperienceItem } from 'tripPlanner/types/tripItem'
import { getOrderId, isLeBookmark } from 'tripPlanner/utils'
import { leExperienceSelectionId } from 'tripPlanner/utils/itemSelection'

interface Props {
  item: ExperienceItem
}

export default function ExperienceSummaryModalFooter({ item }: Props) {
  const intl = useIntl()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const userId = useAppSelector(getCurrentUserId)

  const setImmersive = useCallback(
    () =>
      setImmersiveMode({
        tripId: item.tripId,
        sectionId: leExperienceSelectionId(item.id),
      }),
    [setImmersiveMode, item],
  )

  const orderId = getOrderId(item)

  const experienceId = item.code
  const { data: experience } = useExperience({
    experienceId,
    currentCurrency,
    currentRegionCode,
  })

  if (orderId) {
    if (item.createdBy === userId) {
      return <OwnBookingFooter item={item} orderId={orderId} />
    } else {
      return <ReadOnlyItemFooter item={item} />
    }
  } else if (isLeBookmark(item)) {
    if (!experience) {
      return null
    }
    return (
      <FooterWithCreateUpdateInfo item={item}>
        <TextButton
          kind="primary"
          to={getExperienceOfferPageURL(experience)}
          onClick={setImmersive}
        >
          {intl.formatMessage({
            id: 'edQNbmNgdm',
            defaultMessage: 'Select dates',
            description: 'Button to select dates for an experience booking in trip planner',
          })}
        </TextButton>
      </FooterWithCreateUpdateInfo>
    )
  } else {
    return <EditableItemFooter item={item} />
  }
}
